import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import "../css/DashboardRevenue.css";
import Dashboardfilter from "./Dashboardfilter";
import { motion } from "framer-motion";
import api from "../api";

export default function RevenueChart({ revenue: initialRevenue = {} }) {
  const [chartData, setChartData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 🔹 Format backend revenue object to chart rows
  const formatRevenueData = (revenueObj) => {
    if (!revenueObj || Object.keys(revenueObj).length === 0) return [];

    const colorMap = {
      grooming: "#D61C4E",
      walking: "#FF8A00",
      training: "#28C76F",
      boarding: "#A5661F",
      shopping: "#009DAE",
      doctor_appointment: "#7E22CE",
      home_visit: "#03386D",
      clinic_visit: "#2C0084",
      online_consultation: "#4C4C4C",
    };

    return Object.entries(revenueObj).map(([key, val]) => ({
      day: formatLabel(key),
      amount: Number(val) || 0,
      color: colorMap[key] || "#666",
    }));
  };

  // 🔹 Fetch revenue data (independent from parent)
  const fetchRevenueData = async (from = "", to = "") => {
    try {
      setLoading(true);
      setError("");

      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;

      // ✅ Use centralized API instance
      const res = await api.get("/admin/dashboard/", { params });

      // Only update the revenue chart section
      const revenueData = res.data?.revenue || {};
      setChartData(formatRevenueData(revenueData));
    } catch (err) {
      console.error("Error fetching revenue data:", err);
      setError("Unable to load revenue data");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 On mount (initial load)
  useEffect(() => {
    if (initialRevenue && Object.keys(initialRevenue).length > 0) {
      setChartData(formatRevenueData(initialRevenue));
    } else {
      fetchRevenueData();
    }
  }, []);

  // 🔹 Handle date filter (local only)
  const handleApplyFilter = (from, to) => {
    fetchRevenueData(from, to);
  };

  return (
    <motion.div
      className="revenue-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="revenue-header">
        <h3>Revenue</h3>
        <button className="revenue-filter-btn">
          <Dashboardfilter
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            handleApplyFilter={handleApplyFilter}
          />
        </button>
      </div>

      {loading ? (
        <p className="no-data-text">Loading revenue data...</p>
      ) : error ? (
        <p className="no-data-text">{error}</p>
      ) : chartData.length === 0 ? (
        <p className="no-data-text">No revenue data available.</p>
      ) : (
        <ResponsiveContainer width="100%" height={350} className="revenue-chart">
          <BarChart data={chartData} margin={{ top: 10, bottom: 10, right: 10 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <YAxis
              tickFormatter={(val) => `₹${val / 1000}K`}
              fontSize={12}
              stroke="#333"
            />
            <XAxis
              dataKey="day"
              interval={0}
              tick={({ x, y, payload }) => {
                const lines = payload.value.split("\n");
                const color =
                  chartData.find((d) => d.day === payload.value)?.color || "#333";
                return (
                  <text
                    x={x}
                    y={y + 5}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={700}
                    fill={color}
                  >
                    {lines.map((line, index) => (
                      <tspan key={index} x={x} dy={index === 0 ? 0 : 12}>
                        {line}
                      </tspan>
                    ))}
                  </text>
                );
              }}
            />
            <Tooltip
              formatter={(val) => `₹${Number(val).toLocaleString()}`}
              cursor={{ fill: "#f3f3f3" }}
            />
            <Bar
              dataKey="amount"
              radius={[4, 4, 0, 0]}
              animationDuration={800}
            >
              {chartData.map((entry, index) => (
                <Cell key={`bar-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </motion.div>
  );
}

/* Helper: map backend keys to labels */
function formatLabel(key) {
  const map = {
    grooming: "Grooming",
    walking: "Walking",
    training: "Training",
    boarding: "Boarding",
    shopping: "Shopping",
    doctor_appointment: "Doctor\nAppointment",
    home_visit: "Home\nVisit",
    clinic_visit: "Clinic\nVisit",
    online_consultation: "Online\nConsultation",
  };
  return map[key] || key;
}
